import React, { useEffect, useMemo, useState } from 'react'
import { Button, Descriptions, Input, Modal, Select, Spin, message } from 'antd'
import { FlagOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { fetchVenueSupplierPriceFiles } from 'src/features/VenueSuppliersSlice'
import { createProductIssue } from 'src/utils/productIssueApi'
import {
  PRICE_FILE_ISSUE_CATEGORIES,
  PRICE_FILE_UNSURE_VALUE,
  PRODUCT_ISSUE_EXPLANATION_MAX_LENGTH,
  PRODUCT_ISSUE_SCOPE,
  buildPriceFileIssuePayload,
  formatPriceFileOptionLabel,
  getProductIssueSubmitFeedback,
} from 'src/utils/productIssue'

const { TextArea } = Input
const { Option } = Select

const REPORT_ICON_GREEN = '#52c41a'

const LabelActionButton = styled(Button)`
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  font-weight: 400 !important;
  height: 32px;
  padding: 0 10px;

  .anticon {
    color: ${REPORT_ICON_GREEN};
    margin-right: 6px;
    display: inline-flex;
    align-items: center;
  }
`

function resolveSupplierId(venueSupplier) {
  return (
    venueSupplier?.supplier?._id ||
    venueSupplier?.supplier ||
    venueSupplier?.supplierId ||
    null
  )
}

function resolveSupplierTitle(venueSupplier) {
  return (
    venueSupplier?.supplierTitle || venueSupplier?.supplier?.title || 'Supplier'
  )
}

/**
 * Report a whole price-file / catalogue issue from Your Suppliers.
 */
export default function ReportPriceFileIssueButton({
  venueId,
  venueSupplier,
  disabled = false,
  size,
  type = 'link',
  label = 'Report price file issue',
  style,
  className,
}) {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [category, setCategory] = useState(undefined)
  const [explanation, setExplanation] = useState('')
  const [priceFileChoice, setPriceFileChoice] = useState(undefined)
  const [priceFiles, setPriceFiles] = useState([])
  const [filesLoading, setFilesLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const supplierId = resolveSupplierId(venueSupplier)
  const supplierTitle = resolveSupplierTitle(venueSupplier)
  const canOpen =
    Boolean(venueId && supplierId && venueSupplier?._id) && !disabled

  const currentPriceFileId = String(supplierId || '')

  const priceFileOptions = useMemo(() => {
    if (priceFiles.length > 0) return priceFiles
    if (!supplierId) return []
    return [
      {
        _id: supplierId,
        title: venueSupplier?.supplier?.title || supplierTitle,
        secondTitle: venueSupplier?.supplier?.secondTitle || '',
      },
    ]
  }, [priceFiles, supplierId, supplierTitle, venueSupplier])

  const hasMultiplePriceFiles = priceFileOptions.length > 1

  const reset = () => {
    setCategory(undefined)
    setExplanation('')
    setPriceFileChoice(undefined)
    setSaving(false)
  }

  const handleOpen = e => {
    if (e?.stopPropagation) e.stopPropagation()
    if (!canOpen) return
    reset()
    setOpen(true)
  }

  const handleCancel = () => {
    setOpen(false)
    reset()
  }

  useEffect(() => {
    if (!open || !venueId || !venueSupplier?._id) return undefined

    let cancelled = false
    const load = async () => {
      setFilesLoading(true)
      const result = await dispatch(
        fetchVenueSupplierPriceFiles(venueId, venueSupplier._id),
      )
      if (cancelled) return

      const files = result?.priceFiles || []
      setPriceFiles(files)
      setFilesLoading(false)

      if (files.length === 1) {
        setPriceFileChoice(String(files[0]._id))
      } else if (files.length === 0) {
        setPriceFileChoice(currentPriceFileId)
      } else if (
        currentPriceFileId &&
        files.some(file => String(file._id) === currentPriceFileId)
      ) {
        setPriceFileChoice(currentPriceFileId)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [open, venueId, venueSupplier?._id, dispatch, currentPriceFileId])

  const unsure = priceFileChoice === PRICE_FILE_UNSURE_VALUE
  const selectedPriceFileId = unsure ? undefined : priceFileChoice

  const handleSubmit = async () => {
    if (!category) {
      message.warning('Select what is wrong with the price file')
      return
    }
    if (!priceFileChoice) {
      message.warning('Select the affected price file, or choose unsure')
      return
    }

    setSaving(true)
    try {
      const payload = buildPriceFileIssuePayload({
        supplierId,
        supplierTitle,
        priceFileId: selectedPriceFileId,
        priceFileUnsure: unsure,
        category,
        explanation,
      })
      const result = await createProductIssue(venueId, payload)
      message.success(
        getProductIssueSubmitFeedback({
          routing: result?.routing,
          scope: PRODUCT_ISSUE_SCOPE.PRICE_FILE,
        }),
      )
      setOpen(false)
      reset()
    } catch (error) {
      message.error(error.message || 'Could not submit the price file issue')
      setSaving(false)
    }
  }

  return (
    <>
      <LabelActionButton
        type={type}
        size={size || 'small'}
        onClick={handleOpen}
        disabled={!canOpen}
        style={style}
        className={className}
        data-testid='report-price-file-issue-button'
      >
        <FlagOutlined />
        <span>{label}</span>
      </LabelActionButton>

      <Modal
        title='Report price file issue'
        visible={open}
        onCancel={handleCancel}
        onOk={handleSubmit}
        okText='Submit report'
        confirmLoading={saving}
        destroyOnClose
        width={560}
        okButtonProps={{
          disabled: !category || !priceFileChoice || filesLoading,
        }}
      >
        <p style={{ marginTop: 0, color: '#595959' }}>
          Flag a problem with this supplier&apos;s whole catalogue or price
          file. You do not need to report every product separately.
        </p>

        <Descriptions
          size='small'
          column={1}
          bordered
          style={{ marginBottom: 16 }}
        >
          <Descriptions.Item label='Supplier'>
            {supplierTitle || '—'}
          </Descriptions.Item>
        </Descriptions>

        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 6, fontWeight: 500 }}>
            Affected price file <span style={{ color: '#cf1322' }}>*</span>
          </div>
          {filesLoading ? (
            <Spin size='small' />
          ) : (
            <Select
              placeholder='Select a price file'
              value={priceFileChoice}
              onChange={setPriceFileChoice}
              style={{ width: '100%' }}
              data-testid='report-price-file-issue-file'
            >
              {priceFileOptions.map(file => (
                <Option key={String(file._id)} value={String(file._id)}>
                  {formatPriceFileOptionLabel(file)}
                  {String(file._id) === currentPriceFileId ? ' (current)' : ''}
                </Option>
              ))}
              <Option value={PRICE_FILE_UNSURE_VALUE}>
                I&apos;m not sure which price file
              </Option>
            </Select>
          )}
          {!filesLoading && hasMultiplePriceFiles && (
            <div style={{ marginTop: 6, color: '#8c8c8c', fontSize: 12 }}>
              This supplier has more than one catalogue. Pick the affected one,
              or choose unsure.
            </div>
          )}
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 6, fontWeight: 500 }}>
            What&apos;s wrong with the price file?{' '}
            <span style={{ color: '#cf1322' }}>*</span>
          </div>
          <Select
            placeholder='Select an issue category'
            value={category}
            onChange={setCategory}
            style={{ width: '100%' }}
            data-testid='report-price-file-issue-category'
          >
            {PRICE_FILE_ISSUE_CATEGORIES.map(item => (
              <Option key={item.value} value={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        </div>

        <div>
          <div style={{ marginBottom: 6, fontWeight: 500 }}>
            Please explain the problem{' '}
            <span style={{ color: '#8c8c8c' }}>(optional)</span>
          </div>
          <TextArea
            rows={4}
            value={explanation}
            onChange={e => setExplanation(e.target.value)}
            maxLength={PRODUCT_ISSUE_EXPLANATION_MAX_LENGTH}
            placeholder='e.g. Most prices look out of date. Could the supplier upload a new price file?'
            data-testid='report-price-file-issue-explanation'
          />
        </div>
      </Modal>
    </>
  )
}
